import usePageTitle from "../utils/usePageTitle";
import { processScopeHash } from "../components/processes/processDetailHref";
import React, { useEffect, useMemo, useState } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  ArrowRight,
  ClipboardCheck,
  Clock3,
  FileText,
  LayoutGrid,
  MessageCircle,
  MessageCircleQuestion,
  Signpost,
  Users,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLogin } from "../reducers";
import {
  serializeUserFilterSelection,
  useUserFilterSelection,
} from "../utils/useUserFilterSelection";
import { formatDuration } from "../utils";
import UserFilterMultiSelect from "../components/UserFilterMultiSelect";
import DashboardMetric from "../components/dashboard/DashboardMetric";
import DashboardDateRange from "../components/dashboard/DashboardDateRange";
import { useDashboardDates } from "../components/dashboard/useDashboardDates";
import { useOverviewInsight } from "../components/dashboard/useOverviewInsight";
import { noticedText } from "../components/processes/processNoticedText";
import { useProcessOverview } from "../components/dashboard/useProcessOverview";
import { useSessionsOverview } from "../components/dashboard/useSessionsOverview";
import { previousPeriod, metricDelta } from "../components/dashboard/previousPeriod";
import OverviewAllocation from "../components/dashboard/OverviewAllocation";
import ProcessGraphLink from "../components/dashboard/ProcessGraphLink";
import { type ContextualAsk, useAskSidebar, usePageAsk } from "../chat/AskContext";
import { defaultAskSuggestions, pageDateFilters } from "../chat/askScope";
import { canBrowseTeamSessions } from "../components/dashboard/personalSessionsModel";
import useRecordingControls from "../utils/useRecordingControls";
import { frontendCapabilities } from "../globals";
import { pendingReviewHref } from "../components/dashboard/pendingReviewHref";

export const OVERVIEW_USERS_HASH_KEY = "ou";
export const OVERVIEW_FROM_HASH_KEY = "odf";
export const OVERVIEW_TO_HASH_KEY = "odt";

export default function Overview() {
  const { t } = useTranslation();
  const ask = useAskSidebar();
  const { permissions, user } = useLogin();
  const processPermission = permissions.processes.visualization;
  const canBrowseTeam = canBrowseTeamSessions(processPermission, user.id);
  const { pendingReviewCount } = useRecordingControls();
  usePageTitle(t("dashboard.overview"));
  const {
    selectedUserIds: filteredUserIds,
    userOptions,
    handleUserChange,
    loadingUsers: loadingDirectory,
  } = useUserFilterSelection(OVERVIEW_USERS_HASH_KEY, permissions.processes.visualization);
  // Access determines which people can be selected; the selection determines presentation.
  const selectedUserIds = canBrowseTeam ? filteredUserIds : [user.id];
  const loadingUsers = canBrowseTeam && loadingDirectory;
  const teamView =
    canBrowseTeam && !(selectedUserIds.length === 1 && selectedUserIds[0] === user.id);
  const dates = useDashboardDates();
  const scopeKey = JSON.stringify([[...selectedUserIds].sort(), dates.from, dates.to]);
  const [processSelection, setProcessSelection] = useState<{
    scopeKey: string;
    name: string | null;
  } | null>(null);
  const selectedProcess = processSelection?.scopeKey === scopeKey ? processSelection.name : null;
  const invalidDates = Boolean(dates.from && dates.to && dates.from > dates.to);
  const previous = previousPeriod(dates.from, dates.to);
  const compare = !!previous && !loadingUsers && selectedUserIds.length > 0;
  const previousProcesses = useProcessOverview(
    selectedUserIds,
    previous?.from ?? "",
    previous?.to ?? "",
    compare,
  );
  const previousSessions = useSessionsOverview(
    previous?.from ?? "",
    previous?.to ?? "",
    0,
    selectedUserIds,
    compare,
  );
  const {
    metrics,
    loading: loadingMetrics,
    error,
    reload,
  } = useProcessOverview(selectedUserIds, dates.from, dates.to, !loadingUsers && !invalidDates);
  const sessions = useSessionsOverview(
    dates.from,
    dates.to,
    0,
    selectedUserIds,
    !loadingUsers && !invalidDates,
  );
  const loading = loadingUsers || loadingMetrics;
  const appRows = metrics.apps ?? [];
  const unavailable = loading || error || invalidDates;
  const largest = metrics.processes[0];
  const insight = useOverviewInsight(
    metrics.processes,
    selectedUserIds,
    dates.from,
    dates.to,
    teamView && !unavailable,
  );
  const insightProcess = insight?.process ?? largest;
  const insightPending = insight?.pending ?? false;
  const findingText = insight?.finding ? noticedText(insight.finding, insight.nameById, t) : null;
  const insightHeadline = findingText
    ? t("dashboard.process_insight", {
        process: insightProcess.name,
        finding: findingText.headline,
      })
    : t("dashboard.largest_insight", { process: insightProcess?.name });
  const insightDetail =
    findingText?.detail ??
    t("dashboard.largest_insight_detail", {
      share: String(Math.round((insightProcess?.share ?? 0) * 100)),
    });
  // An empty previous period usually means the range starts at the earliest data.
  const comparisonReady =
    compare &&
    !previousSessions.loading &&
    !previousSessions.failed &&
    !!previousSessions.data?.recordings;
  const processComparisonReady =
    comparisonReady && !unavailable && !previousProcesses.loading && !previousProcesses.error;
  const comparisonDetail = (current: number, prior: number, ready: boolean, percentage = false) => {
    if (!ready || !previous) return undefined;
    const delta = metricDelta(current, prior, percentage);
    return (
      <span title={`${previous.from} – ${previous.to}`} className="text-xs text-text-mid">
        {delta === null
          ? t("dashboard.no_previous_data")
          : t("dashboard.previous_period_delta", { delta })}
      </span>
    );
  };
  // Chat requires both bounds even for the Overview's all-time selection.
  const chatRange = useMemo(() => pageDateFilters(dates.from, dates.to), [dates.from, dates.to]);
  const overviewScopeLabel = `${
    !teamView
      ? t("dashboard.my_work")
      : selectedUserIds.length === userOptions.length
        ? t("watchlist.all_people")
        : userOptions
            .filter(option => selectedUserIds.includes(option.id))
            .map(option => option.label)
            .join(", ")
  } · ${dates.from || dates.to ? `${dates.from} – ${dates.to}` : t("dashboard.all_time")}`;
  const askLabel = (process: string | null) =>
    `${process ? `${process} · ` : ""}${overviewScopeLabel}`;
  const overviewAsk: ContextualAsk | null = chatRange
    ? {
        sessionKey: "overview:context",
        datePreset: dates.preset,
        page: {
          kind: "overview",
          insight:
            !insightPending && (!selectedProcess || selectedProcess === insightProcess?.name)
              ? insightProcess?.name
              : undefined,
        },
        context:
          insightProcess && !unavailable && !insightPending
            ? `Overview key insight: ${insightHeadline} ${insightDetail} Process: ${insightProcess.name}; total work ${formatDuration(insightProcess.seconds)}, average per case ${formatDuration(insightProcess.averageSeconds)}. Observations suggest things to investigate; they do not establish causes or savings.`
            : undefined,
        empty: unavailable || selectedUserIds.length === 0,
        label: askLabel(selectedProcess),
        filters: {
          userIds: selectedUserIds,
          processes: selectedProcess ? [selectedProcess] : undefined,
          ...chatRange,
        },
        suggestions: defaultAskSuggestions(t),
      }
    : null;
  usePageAsk(overviewAsk);
  // Asks about the key insight as the process page asks about its findings: scoped to the
  // insight's process, with the question drafted.
  const askAboutInsight = () => {
    if (!overviewAsk) return;
    ask.openContext(
      {
        ...overviewAsk,
        page: { kind: "overview", insight: insightProcess.name },
        label: askLabel(insightProcess.name),
        filters: { ...overviewAsk.filters, processes: [insightProcess.name] },
      },
      findingText
        ? t("processPanel.noticedAskPrompt", {
            process: insightProcess.name,
            finding: findingText.headline,
            detail: findingText.detail,
          })
        : t("askPanel.insight_prompt", { process: insightProcess.name }),
    );
  };

  const peopleValue = serializeUserFilterSelection(selectedUserIds, userOptions);
  const linkUserIds = peopleValue === "all" ? "all" : selectedUserIds;
  const allProcessesHref = `/processes/?view=all${processScopeHash(linkUserIds, dates)}`;
  // Pending local sessions aren't date-scoped and only belong to the signed-in user.
  const showEmptyReviewPrompt =
    !teamView &&
    frontendCapabilities.localRecording &&
    pendingReviewCount > 0 &&
    !dates.from &&
    !dates.to;

  return (
    <div className="dashboard-page @container">
      <div className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">
            {t(teamView ? "dashboard.process_overview" : "dashboard.my_work")}
          </h1>
          {!teamView && (
            <p className="dashboard-page-description">{t("dashboard.my_work_subtitle")}</p>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {canBrowseTeam && userOptions.length > 1 && (
            <div className="dashboard-people-filter">
              <UserFilterMultiSelect
                allLabel={t("watchlist.all_people")}
                icon={<Users aria-hidden="true" className="h-4 w-4 shrink-0 text-text-dim" />}
                value={selectedUserIds}
                options={userOptions}
                onChange={handleUserChange}
              />
            </div>
          )}
          <DashboardDateRange {...dates} activityUserIds={selectedUserIds} />
        </div>
      </div>
      <div className="mb-6 mt-4 grid grid-cols-1 gap-4 @[400px]:grid-cols-2 @[900px]:grid-cols-4">
        {[
          {
            label: t("dashboard.total_process_time"),
            value: unavailable ? null : formatDuration(metrics.seconds),
            icon: Clock3,
            detail: comparisonDetail(
              metrics.seconds,
              previousProcesses.metrics.seconds,
              processComparisonReady,
              true,
            ),
            title: t("dashboard.active_time_help"),
            loading,
          },
          {
            label: t("dashboard.processes"),
            value: unavailable ? null : metrics.processes.length,
            detail: comparisonDetail(
              metrics.processes.length,
              previousProcesses.metrics.processes.length,
              processComparisonReady,
            ),
            icon: FileText,
            loading,
          },
          {
            label: t("dashboard.apps"),
            value: unavailable ? null : metrics.appCount,
            detail: comparisonDetail(
              metrics.appCount,
              previousProcesses.metrics.appCount,
              processComparisonReady,
            ),
            icon: LayoutGrid,
            loading,
          },
          {
            label: t(teamView ? "dashboard.people_observed" : "dashboard.sessions_captured"),
            value:
              invalidDates || sessions.failed || !sessions.data
                ? null
                : teamView
                  ? sessions.data.people.length
                  : sessions.data.recordings,
            icon: teamView ? Users : FileText,
            detail: comparisonDetail(
              (teamView ? sessions.data?.people.length : sessions.data?.recordings) ?? 0,
              (teamView
                ? previousSessions.data?.people.length
                : previousSessions.data?.recordings) ?? 0,
              comparisonReady && !sessions.loading && !sessions.failed && !!sessions.data,
            ),
            title: t(
              teamView ? "dashboard.people_observed_help" : "dashboard.sessions_captured_help",
            ),
            loading: loadingUsers || sessions.loading,
            retry: sessions.failed && !invalidDates ? sessions.reload : undefined,
          },
        ].map(({ label, value, icon: Icon, title, loading: metricLoading, retry, detail }) => (
          <section
            key={label}
            aria-label={label}
            aria-busy={metricLoading}
            className="min-w-0 rounded-xl border border-outline bg-surface px-5 py-4"
          >
            <DashboardMetric
              label={label}
              icon={
                <span className="inline-flex shrink-0 rounded-full bg-info/10 p-2 text-info">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
              }
              value={value}
              title={title}
              loading={metricLoading}
              detail={detail}
            />
            {retry && (
              <button type="button" onClick={retry} className="text-sm text-info underline">
                {t("retry")}
              </button>
            )}
          </section>
        ))}
      </div>
      {!unavailable && !teamView && largest && largest.seconds > 0 && (
        <section
          aria-labelledby="overview-review-title"
          className="mb-5 rounded-xl border border-info/15 bg-surface p-1.5"
        >
          <div className="flex flex-col gap-5 rounded-lg bg-info/5 p-5 @[800px]:flex-row @[800px]:items-center @[800px]:gap-8 @[800px]:px-6 @[800px]:py-5">
            <div className="flex min-w-0 flex-1 flex-col items-start gap-5 @[500px]:flex-row @[500px]:items-center">
              <span
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-info/10 text-info @[800px]:h-20 @[800px]:w-20"
                aria-hidden="true"
              >
                <ClipboardCheck
                  className="h-10 w-10 @[800px]:h-11 @[800px]:w-11"
                  strokeWidth={1.5}
                />
              </span>
              <div className="min-w-0">
                <h2
                  id="overview-review-title"
                  className="font-display text-lg font-semibold leading-snug tracking-tight text-text"
                >
                  {t("dashboard.review_work_title")}
                </h2>
                <p className="mt-1 max-w-md text-sm leading-relaxed text-text-mid">
                  {t("dashboard.review_work_description")}
                </p>
              </div>
            </div>
            <Link
              to={allProcessesHref}
              className="group inline-flex w-full shrink-0 items-center justify-center gap-3 rounded-lg bg-info px-4 py-3.5 text-sm font-medium text-white transition-colors hover:bg-info-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-info @[500px]:ml-[84px] @[500px]:w-auto @[500px]:self-start @[500px]:px-6 @[800px]:ml-0 @[800px]:self-center"
            >
              {t("dashboard.review_my_processes")}
              <ArrowRight
                className="h-4 w-4 shrink-0 motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
          </div>
        </section>
      )}
      {!unavailable && teamView && largest && largest.seconds > 0 && (
        <section
          aria-labelledby={insightPending ? "overview-insight-label" : "overview-insight-title"}
          aria-busy={insightPending}
          className="mb-5 rounded-xl border border-insight-line bg-insight-soft p-6"
        >
          <div className="flex flex-col gap-5 @[1100px]:flex-row @[1100px]:items-center @[1100px]:gap-8">
            <div className="min-w-0 flex-1">
              <p
                id="overview-insight-label"
                className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-insight"
              >
                <Zap className="h-4 w-4" aria-hidden="true" />
                {t("dashboard.key_insight")}
              </p>
              {insightPending ? (
                // A placeholder rather than the stand-in, which the finding would replace.
                <div aria-hidden="true">
                  <span className="block h-7 w-3/4 animate-pulse rounded bg-insight/10" />
                  <span className="mt-3 block h-4 w-full max-w-xl animate-pulse rounded bg-insight/10" />
                </div>
              ) : (
                <>
                  <h2
                    id="overview-insight-title"
                    className="font-display text-xl font-semibold leading-snug tracking-tight"
                  >
                    {insightHeadline}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-text-mid">{insightDetail}</p>
                </>
              )}
            </div>
            {insightPending ? (
              <span
                aria-hidden="true"
                className="block h-11 w-64 max-w-full animate-pulse rounded-lg bg-insight/10"
              />
            ) : (
              <div className="flex flex-wrap items-center gap-x-7 gap-y-4">
                <dl className="flex gap-7">
                  {[
                    [t("dashboard.process_time"), formatDuration(insightProcess.seconds)],
                    insight?.finding
                      ? [t("processPanel.metricCases"), String(insightProcess.count)]
                      : [t("dashboard.average_run"), formatDuration(insightProcess.averageSeconds)],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="@[1100px]:border-l @[1100px]:border-insight-line @[1100px]:pl-7"
                    >
                      <dt className="text-xs text-insight">{label}</dt>
                      <dd className="mt-1 font-display text-2xl font-semibold tabular-nums whitespace-nowrap">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
                <div className="flex items-center gap-2">
                  <ProcessGraphLink
                    slug={insightProcess.slug}
                    name={insightProcess.name}
                    userIds={linkUserIds}
                    showLabel
                    prominent
                  />
                  {overviewAsk && (
                    <Tooltip.Provider delayDuration={200}>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <button
                            type="button"
                            aria-label={t("processPanel.noticedAsk")}
                            aria-controls="ask-sidebar"
                            aria-expanded={ask.open}
                            onClick={askAboutInsight}
                            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-insight-line text-insight transition-colors hover:bg-insight/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-insight"
                          >
                            <MessageCircleQuestion className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="top"
                            sideOffset={6}
                            collisionPadding={8}
                            className="pointer-events-none z-[100] rounded-lg border border-outline bg-surface px-3 py-2 text-xs text-text shadow-lg"
                          >
                            {t("processPanel.noticedAsk")}
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
      {invalidDates ? (
        <p role="alert" className="text-sm text-warning">
          {t("dashboard.invalid_dates")}
        </p>
      ) : error ? (
        <div role="alert" className="rounded-xl border border-outline bg-surface p-6 text-sm">
          {t("dashboard.overview_load_failed")}{" "}
          <button type="button" className="ml-2 text-info underline" onClick={reload}>
            {t("retry")}
          </button>
        </div>
      ) : loading ? (
        <p
          role="status"
          className="rounded-2xl border border-outline bg-surface p-10 text-center text-text-dim"
        >
          {t("loading_ellipsis")}
        </p>
      ) : !metrics.processes.length && !appRows.length ? (
        <div className="rounded-2xl border border-outline bg-surface p-10 text-center">
          <p className="text-sm text-text-dim">
            {t(
              showEmptyReviewPrompt
                ? "dashboard.empty_review_sessions"
                : "dashboard.empty_processes",
            )}
          </p>
          <Link
            className="mt-4 inline-block rounded text-sm font-medium text-info hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info"
            to={showEmptyReviewPrompt ? pendingReviewHref(user.id) : allProcessesHref}
          >
            {t(
              showEmptyReviewPrompt
                ? "dashboard.pending_review_action"
                : "dashboard.view_all_processes",
            )}
          </Link>
        </div>
      ) : (
        <OverviewAllocation
          key={scopeKey}
          onSelectionChange={name => setProcessSelection({ scopeKey, name })}
          metrics={metrics}
          previousMetrics={processComparisonReady ? previousProcesses.metrics : undefined}
          comparison={compare && previous ? previous : undefined}
          personal={!teamView}
          linkUserIds={linkUserIds}
        />
      )}
      {teamView &&
        !unavailable &&
        overviewAsk &&
        (metrics.processes.length > 0 || appRows.length > 0) && (
          <section
            aria-labelledby="overview-next-step-title"
            className="mt-5 flex flex-col gap-5 rounded-xl border border-info/15 bg-surface px-5 py-4 @[900px]:flex-row @[900px]:items-center @[900px]:gap-6"
          >
            <div className="flex min-w-0 flex-1 items-center gap-4">
              <span className="rounded-lg bg-info/5 p-3 text-info" aria-hidden="true">
                <Signpost className="h-6 w-6" strokeWidth={1.5} />
              </span>
              <div>
                <h2
                  id="overview-next-step-title"
                  className="text-xs font-semibold uppercase tracking-wider text-text-mid"
                >
                  {t("dashboard.next_step")}
                </h2>
                <p className="mt-1 text-sm font-medium text-text">
                  {t("dashboard.next_step_description")}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-info/15 pt-4 @[900px]:shrink-0 @[900px]:gap-7 @[900px]:border-l @[900px]:border-t-0 @[900px]:pl-6 @[900px]:pt-0">
              <p className="text-sm leading-relaxed text-text-mid">
                {t("dashboard.next_step_hint")}
                <br />
                {t("dashboard.next_step_ask")}
              </p>
              <button
                type="button"
                aria-controls="ask-sidebar"
                aria-expanded={ask.open}
                onClick={() => ask.openContext(overviewAsk)}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-info px-5 py-2 text-sm font-medium text-info transition-colors hover:bg-info/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                {t("dashboard.ask")}
              </button>
            </div>
          </section>
        )}
    </div>
  );
}
